import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { staffSeatsLeft } from '@telestore/contracts';
import { checkPrimary } from '@telestore/theming';
import { PROVINCES, parseAmount, toDecimal } from '@telestore/shared';
import { Bot as BotIcon, Check, Lock, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { openTelegramUrl } from '../lib/telegram-link.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  CopyButton,
  Empty,
  ErrorBox,
  Field,
  Input,
  Panel,
  Pill,
  Row,
  Rows,
  Spinner,
} from '../components/ui.js';

/**
 * Shop settings. Grouped by the onboarding order from §8's week 5–6: theme,
 * delivery, KHQR, the bot and its ABA group, then the team.
 */
export function SettingsPage() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const shop = useQuery({
    queryKey: ['shop', shopId],
    queryFn: () => api.shop(shopId!),
    enabled: Boolean(shopId),
  });

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateShop(shopId!, patch),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['shop', shopId] });
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  if (shop.isPending) return <Spinner label={t('common.loading')} />;
  if (shop.isError || !shop.data) {
    return <ErrorBox message={(shop.error as Error | undefined)?.message ?? t('errors.internal')} />;
  }

  const data = shop.data;

  return (
    <div className="flex flex-col gap-4">
      {error ? <ErrorBox message={error} /> : null}

      <ShopBasics data={data} onSave={(patch) => save.mutate(patch)} saving={save.isPending} />
      <ThemePicker data={data} onSave={(patch) => save.mutate(patch)} />
      <KhqrSetup data={data} onSave={(patch) => save.mutate(patch)} />
      <ShippingRates />
      <BotSetup data={data} />
      <TeamPanel />

      <Panel title={t('admin.exportData')}>
        <p className="mb-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {/* §6.8: no lock-in — the data is theirs and always exportable. */}
          {t('admin.exportData')}
        </p>
        <a className="btn btn-quiet" href={api.exportOrdersUrl(shopId!)} download>
          orders.csv
        </a>
      </Panel>

      <p className="text-center text-xs" style={{ color: 'var(--color-ink-faint)' }}>
        {locale === 'km' ? 'ហាង' : 'Shop'}: {data.storefrontUrl}
      </p>
    </div>
  );
}

function ShopBasics({
  data,
  onSave,
  saving,
}: {
  data: Awaited<ReturnType<typeof api.shop>>;
  onSave(patch: Record<string, unknown>): void;
  saving: boolean;
}) {
  const { t } = useSession();
  const [name, setName] = useState(data.shop.name);
  const [slug, setSlug] = useState(data.shop.slug);
  const [telegram, setTelegram] = useState(data.shop.settings.contact_telegram_username ?? '');
  const [phone, setPhone] = useState(data.shop.settings.contact_phone ?? '');
  const [autoConfirm, setAutoConfirm] = useState(data.shop.settings.auto_confirm_orders);
  const [cod, setCod] = useState(data.shop.settings.cod_enabled);
  const [prepaid, setPrepaid] = useState(data.shop.settings.prepaid_enabled);

  return (
    <Panel
      title={t('admin.settings')}
      action={
        <Button disabled={saving} onClick={() => onSave({ name, slug, settings: { contact_telegram_username: telegram.replace(/^@/, '') || null, contact_phone: phone || null, auto_confirm_orders: autoConfirm, cod_enabled: cod, prepaid_enabled: prepaid, }, }) } >
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('checkout.name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field
          label="Address"
          // §6.8: a renamed slug keeps redirecting forever, so this is safe.
          hint="Old links keep working forever"
        >
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
        </Field>
        <Field label={t('storefront.chatWithShop')} hint="@username">
          <Input value={telegram} onChange={(e) => setTelegram(e.target.value)} />
        </Field>
        <Field label={t('checkout.phone')}>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
      </div>

      <div className="mt-4 flex flex-col gap-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={prepaid} onChange={(e) => setPrepaid(e.target.checked)} />
          {t('checkout.payByTransfer')}
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={cod} onChange={(e) => setCod(e.target.checked)} />
          {t('checkout.payOnDelivery')}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={autoConfirm}
            onChange={(e) => setAutoConfirm(e.target.checked)}
          />
          {/* Auto-confirm shows the customer their QR immediately. */}
          {t('merchant.confirmOrder')} — automatic
        </label>
      </div>
    </Panel>
  );
}

function ThemePicker({
  data,
  onSave,
}: {
  data: Awaited<ReturnType<typeof api.shop>>;
  onSave(patch: Record<string, unknown>): void;
}) {
  const { locale, t } = useSession();
  const [primary, setPrimary] = useState(
    data.shop.theme.overrides?.['color-primary'] ?? '',
  );

  // §5: auto contrast guardrail, shown live as the merchant picks.
  const contrast = primary ? checkPrimary(primary) : null;

  return (
    <Panel title={t('admin.theme')}>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {data.templates.map((template) => {
          const active = data.shop.theme.template === template.id;
          return (
            <button
              key={template.id}
              type="button"
              /**
               * `min-w-0` on the card itself, not just on the text inside it.
               * A grid item defaults to `min-width: auto`, so the track refuses
               * to shrink below the item's min-content width — and `truncate`
               * sets `white-space: nowrap`, which makes that min-content the
               * full untruncated blurb. The result was a one-column phone
               * layout whose cards ran off the right edge: the inner `min-w-0`
               * let the text shrink within the card, but nothing let the card
               * shrink within the grid.
               */
              className="panel flex min-w-0 items-center gap-3 p-3 text-left"
              style={active ? { borderColor: 'var(--color-brand)', borderWidth: 2 } : undefined}
              onClick={() => onSave({ theme: { template: template.id } })}
            >
              <span
                className="size-9 shrink-0 rounded-lg"
                style={{
                  background: template.tokens['color-primary'],
                  border: `2px solid ${template.tokens['color-bg']}`,
                  boxShadow: '0 0 0 1px var(--color-line)',
                }}
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{template.name}</span>
                <span className="block truncate text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                  {locale === 'km' ? template.blurb.km : template.blurb.en}
                </span>
              </span>
              {/* Never squeezed: "Pro" is the reason this card is not clickable yet. */}
              {template.premium ? (
                <span className="shrink-0">
                  <Pill tone="warn">Pro</Pill>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.color')} hint="#RRGGBB">
          <Input
            className="max-w-36"
            value={primary}
            placeholder="#db2777"
            onChange={(e) => setPrimary(e.target.value)}
          />
        </Field>
        {contrast ? (
          <Pill tone={contrast.passesAA ? 'positive' : 'warn'}>
            {contrast.ratio}:1{' '}
            {contrast.passesAA ? 'AA' : <TriangleAlert className="inline size-3" />}
          </Pill>
        ) : null}
        <Button variant="outline" disabled={!/^#[0-9a-fA-F]{6}$/.test(primary)} onClick={() => onSave({ theme: { overrides: { 'color-primary': primary } } })} >
          {t('common.save')}
        </Button>
      </div>
    </Panel>
  );
}

function KhqrSetup({
  data,
  onSave,
}: {
  data: Awaited<ReturnType<typeof api.shop>>;
  onSave(patch: Record<string, unknown>): void;
}) {
  const { t } = useSession();
  const khqr = data.shop.settings.khqr;
  const [accountId, setAccountId] = useState(khqr?.bakong_account_id ?? '');
  const [merchantName, setMerchantName] = useState(khqr?.merchant_name ?? '');
  const [city, setCity] = useState(khqr?.merchant_city ?? 'Phnom Penh');
  const [label, setLabel] = useState(khqr?.account_label ?? '');

  const ready = accountId.trim().length > 2 && merchantName.trim().length > 0;

  return (
    <Panel
      title={t('admin.khqrSetup')}
      action={khqr ? <Pill tone="positive">✓</Pill> : <Pill tone="warn">!</Pill>}
    >
      <p className="mb-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
        {t('admin.khqrSetupHint')}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Bakong account id" hint="name@aba">
          <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} />
        </Field>
        <Field label="Merchant name" hint="Latin only, max 25 characters">
          <Input
 
            maxLength={25}
            value={merchantName}
            onChange={(e) => setMerchantName(e.target.value)}
          />
        </Field>
        <Field label="City">
          <Input maxLength={15} value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label={t('payment.payTo')} hint={t('common.optional')}>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
      </div>
      <Button className="mt-3" disabled={!ready} onClick={() => onSave({ settings: { khqr: { bakong_account_id: accountId.trim(), merchant_name: merchantName.trim(), merchant_city: city.trim() || 'Phnom Penh', ...(label.trim() ? { account_label: label.trim() } : {}), }, }, }) } >
        {t('common.save')}
      </Button>
    </Panel>
  );
}

function ShippingRates() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});

  const shipping = useQuery({
    queryKey: ['shipping', shopId],
    queryFn: () => api.shipping(shopId!),
    enabled: Boolean(shopId),
  });

  const save = useMutation({
    mutationFn: () => {
      const currency = shipping.data!.currency;
      const rates = Object.entries(draft)
        .filter(([, value]) => value.trim() !== '')
        .map(([provinceCode, value]) => ({
          provinceCode,
          priceAmount: parseAmount(value, currency).amount,
          isActive: true,
        }));
      return api.saveShipping(shopId!, rates);
    },
    onSuccess: () => {
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['shipping', shopId] });
    },
  });

  if (shipping.isPending) return <Panel title={t('admin.delivery')}><Spinner /></Panel>;
  if (!shipping.data) return null;

  const existing = new Map(shipping.data.rates.map((rate) => [rate.provinceCode, rate]));
  const currency = shipping.data.currency;

  return (
    <Panel
      title={t('admin.delivery')}
      action={
        <Button disabled={Object.keys(draft).length === 0 || save.isPending} onClick={() => save.mutate()} >
          {t('common.save')}
        </Button>
      }
      padded={false}
    >
      <p className="px-4 pt-3 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
        {/* §3 delivery: the merchant's table IS the calculation. */}
        {t('common.shipping')} — {currency}
      </p>
      <div className="grid gap-x-4 p-4 sm:grid-cols-2">
        {PROVINCES.map((province) => {
          const rate = existing.get(province.code);
          const value =
            draft[province.code] ?? (rate ? String(toDecimal({ amount: rate.price, currency })) : '');
          return (
            /* `min-w-0` for the same reason as the theme cards above: a grid
               item will not shrink below its min-content width, and the
               truncating label sets `white-space: nowrap`. Measured as fitting
               today at 360px — this is the guard against the next longer
               province name, not a fix for a live overflow. */
            <label key={province.code} className="flex min-w-0 items-center gap-2 py-1">
              <span className="min-w-0 flex-1 truncate text-sm">
                {locale === 'km' ? province.km : province.en}
              </span>
              <Input
                className="w-24 text-right tabular-nums"
                inputMode="decimal"
                placeholder="—"
                value={value}
                onChange={(event) =>
                setDraft((current) => ({ ...current, [province.code]: event.target.value }))
                }
              />
            </label>
          );
        })}
      </div>
    </Panel>
  );
}

function BotSetup({ data }: { data: Awaited<ReturnType<typeof api.shop>> }) {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [steps, setSteps] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [createLink, setCreateLink] = useState<string | null>(null);

  /**
   * Opens Telegram rather than doing the work itself: the whole point of the
   * managed-bot link is that the creation UI is Telegram's, not ours.
   */
  const provision = useMutation({
    mutationFn: () => api.provisionBot(shopId!),
    onSuccess: (result) => {
      /**
       * Keep the link either way. Opening it here is a convenience that can
       * fail for reasons the merchant cannot see — a popup blocker on the web,
       * a webview that ignores `window.open` inside Telegram — so the URL is
       * also rendered as a real link they can tap.
       */
      setCreateLink(result.createLink);
      openTelegramUrl(result.createLink);
      setMessage(null);
    },
    onError: (cause) => setMessage(cause instanceof ApiError ? cause.message : 'Failed'),
  });

  const attach = useMutation({
    mutationFn: () => api.attachBot(shopId!, token.trim()),
    onSuccess: (result) => {
      setToken('');
      setSteps(result.manualStepsRequired);
      setMessage(result.bot.username ? `@${result.bot.username}` : null);
      void queryClient.invalidateQueries({ queryKey: ['shop', shopId] });
    },
    onError: (cause) => setMessage(cause instanceof ApiError ? cause.message : 'Failed'),
  });

  const setGroup = useMutation({
    mutationFn: () => api.setAbaGroup(shopId!, Number(chatId)),
    onSuccess: (result) => {
      setMessage(result.fallbackMessage ?? `listener: ${result.listenerState}`);
      void queryClient.invalidateQueries({ queryKey: ['shop', shopId] });
    },
    onError: (cause) => setMessage(cause instanceof ApiError ? cause.message : 'Failed'),
  });

  const bot = data.bot;

  return (
    <Panel
      title="Bot"
      action={
        <Pill tone={bot.state === 'active' ? 'positive' : 'warn'}>
          {bot.username ? `@${bot.username}` : bot.state}
        </Pill>
      }
    >
      {bot.state !== 'active' ? (
        <div className="flex flex-col gap-3">
          {/*
            The Managed Bots path first (§3, Bot API 10.0). One tap opens
            Telegram's own creation screen with the name and username filled
            in, and the token comes back to us over the `managed_bot` update —
            the merchant never sees a credential, and there is nothing for
            them to paste into the wrong window.
          */}
          <Button
            className="self-start"
            disabled={provision.isPending}
            onClick={() => provision.mutate()}
          >
            <BotIcon size={16} />
            {t('bot.createForMe')}
          </Button>
          <p className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
            {t('bot.createForMeHint')}
          </p>

          {/*
            Shown once the link exists, because opening it automatically is not
            something we can verify from here. No `target="_blank"`: Telegram's
            own SDK only rewrites t.me anchors that do not have it, and inside
            a Mini App that rewrite is the thing that makes the link work.
          */}
          {createLink ? (
            <a
              href={createLink}
              className="self-start text-xs underline"
              style={{ color: 'var(--color-brand)' }}
            >
              {t('bot.openInTelegram')}
            </a>
          ) : null}

          <div className="my-1 flex items-center gap-3">
            <span className="h-px flex-1" style={{ background: 'var(--color-line)' }} />
            <span className="text-xs" style={{ color: 'var(--color-ink-faint)' }}>
              {t('bot.orPasteToken')}
            </span>
            <span className="h-px flex-1" style={{ background: 'var(--color-line)' }} />
          </div>

          <Field label="Bot token" hint="Create a bot in @BotFather and paste its token">
            <Input
              className="font-mono text-xs"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="1234567890:AA…"
            />
          </Field>
          <Button className="self-start" disabled={attach.isPending || token.trim().length < 30} onClick={() => attach.mutate()} >
            {t('common.confirm')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <Pill tone={bot.b2bMode ? 'positive' : 'warn'}>Groups {bot.b2bMode ? 'on' : 'off'}</Pill>
            <Pill tone={bot.privacyModeOff ? 'positive' : 'warn'}>
              Privacy {bot.privacyModeOff ? 'off' : 'on'}{' '}
              {bot.privacyModeOff ? (
                <Check className="inline size-3" />
              ) : (
                <TriangleAlert className="inline size-3" />
              )}
            </Pill>
            <Pill
              tone={
                bot.abaListenerState === 'active'
                  ? 'positive'
                  : bot.abaListenerState === 'off'
                    ? 'neutral'
                    : 'warn'
              }
            >
              ABA {bot.abaListenerState}
            </Pill>
          </div>

          <div>
            <p className="mb-2 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
              {t('admin.abaGroupHint')}
            </p>
            <div className="flex items-end gap-2">
              <Field label={t('admin.abaGroupSetup')} hint="Group chat id (negative number)">
                <Input
                  className="max-w-48"
                  value={chatId}
                  onChange={(event) => setChatId(event.target.value)}
                  placeholder="-1001234567890"
                />
              </Field>
              <Button disabled={setGroup.isPending || !/^-?\d{5,}$/.test(chatId)} onClick={() => setGroup.mutate()} >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {steps.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 text-sm" style={{ color: 'var(--color-warn)' }}>
          {steps.map((step) => (
            <li key={step}>• {step}</li>
          ))}
        </ul>
      ) : null}

      {message ? (
        <p className="mt-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {message}
        </p>
      ) : null}
    </Panel>
  );
}

function TeamPanel() {
  const { shopId, t } = useSession();
  const [link, setLink] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['members', shopId],
    queryFn: () => api.members(shopId!),
    enabled: Boolean(shopId),
  });

  /**
   * The plan, only so the button can tell the truth before it is pressed.
   *
   * Shares `Shell`'s query key, so this is the cache rather than a second
   * request.
   */
  const shop = useQuery({
    queryKey: ['shop', shopId],
    queryFn: () => api.shop(shopId!),
    enabled: Boolean(shopId),
    staleTime: 5 * 60_000,
  });

  const invite = useMutation({
    mutationFn: () => api.inviteStaff(shopId!, 'staff'),
    onSuccess: (result) => setLink(result.link),
  });

  /**
   * Locked, not broken (§10: "locked features stay visible in admin with a
   * lock").
   *
   * `staffSeatsLeft` is the same function the server refuses with, so the
   * button cannot say yes to something the API says no to. Free has one seat
   * and the owner is in it, which is why this is the normal state of a free
   * shop rather than an edge case — and why the message names the fix instead
   * of reporting a failure.
   *
   * While the plan is still loading, assume there IS room: a button that
   * flickers into a locked state is worse than one that briefly allows a click
   * the server would answer properly anyway.
   */
  const activeMembers = members.data?.members.filter((m) => m.status === 'active').length ?? 0;
  const plan = shop.data?.shop.plan;
  const seatsLeft = shop.data ? staffSeatsLeft(plan!, activeMembers) : 1;
  const full = seatsLeft === 0;
  // Seats at zero members IS the cap, and it goes through the same guarded
  // lookup — reading `PLANS[plan]` directly here is what crashed the dashboard.
  const seatCap = staffSeatsLeft(plan!, 0);

  return (
    <Panel
      title={t('admin.team')}
      action={
        <Button
          variant="outline"
          disabled={invite.isPending || full}
          onClick={() => invite.mutate()}
          title={full ? t('admin.staffCapHint') : undefined}
        >
          {full ? <Lock /> : '+'} {t('admin.inviteStaff')}
        </Button>
      }
      padded={false}
    >
      {full ? (
        <div
          className="flex items-center gap-2 px-4 py-3 text-xs"
          style={{ background: 'var(--color-brand-soft)', color: 'var(--color-brand)' }}
        >
          <Lock className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            {t('admin.staffCapHint', { count: seatCap })}
          </span>
          <Link to="/billing">
            <Button size="sm">{t('admin.upgrade')}</Button>
          </Link>
        </div>
      ) : null}
      {link ? (
        <div className="flex items-center gap-2 p-4" style={{ background: 'var(--color-brand-soft)' }}>
          <code className="min-w-0 flex-1 truncate text-xs">{link}</code>
          <CopyButton value={link} label={t('common.copy')} copiedLabel={t('common.copied')} />
        </div>
      ) : null}

      {members.isPending ? (
        <Spinner />
      ) : (members.data?.members.length ?? 0) === 0 ? (
        <Empty title={t('common.empty')} />
      ) : (
        <Rows>
          {members.data!.members.map((member) => (
            <Row key={member.userId}>
              <span className="flex-1 text-sm">
                {[member.firstName, member.lastName].filter(Boolean).join(' ')}
                {member.username ? (
                  <span style={{ color: 'var(--color-ink-faint)' }}> @{member.username}</span>
                ) : null}
              </span>
              <Pill tone={member.role === 'owner' ? 'brand' : 'neutral'}>{member.role}</Pill>
            </Row>
          ))}
        </Rows>
      )}
    </Panel>
  );
}
